// Löschen-Dialog (Handoff Sonstige Kontakte §3, Screen 10). Löschen wie bisher über loescheKontakt;
// „Abbrechen“ schließt nur den Dialog (früher sprang es zur Liste zurück – Entscheidung 8).
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CircleNotch, Trash } from '@phosphor-icons/react';
import { useDialogFokus } from '../../../../components/immobilien/ui/fokus';
import { loescheKontakt } from '../../../../lib/kontakte/api';

export function KontaktLoeschenDialog({ kontaktId, name, onAbbrechen, onGeloescht }: {
  kontaktId: string;
  name: string;
  onAbbrechen: () => void;
  onGeloescht: () => void;
}) {
  const fenster = useDialogFokus<HTMLDivElement>();
  const abbrechen = useRef<HTMLButtonElement>(null);
  const [loescht, setLoescht] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    abbrechen.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !loescht) onAbbrechen(); };
    window.addEventListener('keydown', esc);
    const html = document.documentElement;
    const vorher = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', esc); html.style.overflow = vorher; };
  }, [onAbbrechen, loescht]);

  const loeschen = async () => {
    setLoescht(true);
    setFehler(null);
    try {
      await loescheKontakt(kontaktId);
      onGeloescht();
    } catch (err: unknown) {
      setFehler('Fehler beim Löschen des Kontakts: ' + ((err as { message?: string } | null)?.message ?? 'Unbekannter Fehler'));
      setLoescht(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 420, padding: 20, fontFamily: "'Manrope', -apple-system, sans-serif" }}>
      <div aria-hidden onMouseDown={() => { if (!loescht) onAbbrechen(); }} className="absolute inset-0"
        style={{ background: 'rgba(10,14,22,0.32)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }} />
      <div ref={fenster} role="alertdialog" aria-modal="true" aria-labelledby="sk-loeschen-titel" aria-describedby="sk-loeschen-text" tabIndex={-1}
        className="sk-flaeche sk-dialog-in relative flex w-full flex-col items-center text-center outline-none"
        style={{ maxWidth: 400, borderRadius: 30, padding: 24, gap: 14, color: 'var(--ink)', background: 'var(--sheet)', backdropFilter: 'blur(40px) saturate(180%)', WebkitBackdropFilter: 'blur(40px) saturate(180%)', boxShadow: 'var(--glass-sh-hv)' }}>
        <span aria-hidden className="flex items-center justify-center"
          style={{ width: 60, height: 60, borderRadius: 20, color: 'var(--sk-av-ink)', background: 'linear-gradient(155deg, var(--sk-loeschen-a), var(--sk-loeschen-b))', boxShadow: 'inset 0 2px 0 rgba(255,255,255,0.4), 0 14px 24px -10px var(--sk-loeschen-b)', transform: 'rotate(-6deg)' }}>
          <Trash weight="fill" size={28} />
        </span>
        <h2 id="sk-loeschen-titel" className="m-0" style={{ fontSize: 19, fontWeight: 800 }}>Kontakt löschen?</h2>
        <p id="sk-loeschen-text" className="m-0" style={{ fontSize: 13.5, lineHeight: 1.55, color: 'var(--ink2)', textWrap: 'pretty' }}>
          <strong style={{ color: 'var(--ink)' }}>{name}</strong> wird unwiderruflich gelöscht. Verknüpfungen und Kontakt-Kanäle werden mitentfernt.
        </p>
        {fehler && <p role="alert" className="m-0" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--bad)', overflowWrap: 'anywhere' }}>{fehler}</p>}
        <div className="flex w-full flex-col" style={{ gap: 8, marginTop: 4 }}>
          <button type="button" onClick={() => void loeschen()} disabled={loescht} className="sk-akzent flex cursor-pointer items-center justify-center disabled:opacity-60"
            style={{ height: 46, border: 'none', borderRadius: 23, gap: 8, fontSize: 14, fontWeight: 800, color: 'var(--sk-av-ink)', background: 'var(--sk-loeschen-knopf)' }}>
            {loescht ? <CircleNotch size={15} className="animate-spin" /> : <Trash weight="bold" size={15} />}
            Endgültig löschen
          </button>
          <button ref={abbrechen} type="button" onClick={onAbbrechen} disabled={loescht} className="sk-chip cursor-pointer disabled:opacity-60"
            style={{ height: 44, border: 'none', borderRadius: 22, fontSize: 14, fontWeight: 700, color: 'var(--ink2)' }}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
